import React from 'react';

/*
 * Icon
 *
 * Font Awesome, loaded as a kit from index.html. The kit swaps each <i> for an
 * <svg> after it loads, so nothing is bundled and no icon package needs to be
 * kept in step with the designs.
 *
 * Names are the Bootstrap Icons vocabulary the rest of the site already speaks,
 * mapped here to their Font Awesome equivalents. Keeping the old names means
 * every call site stays as it was - the swap is this file and a script tag.
 *
 * Size is set as font-size rather than width and height: Font Awesome's glyphs
 * are sized in em, so one number scales the whole thing and it inherits color
 * from `currentColor` the way text does.
 */
const ICONS = {
    'activity': 'fa-solid fa-chart-line',
    'apple': 'fa-brands fa-apple',
    'arrow-repeat': 'fa-solid fa-arrows-rotate',
    'arrow-right': 'fa-solid fa-arrow-right',
    'arrows-fullscreen': 'fa-solid fa-expand',
    'asterisk': 'fa-solid fa-asterisk',
    'badge-wc-fill': 'fa-solid fa-restroom',
    'basket2': 'fa-solid fa-basket-shopping',
    'battery-full': 'fa-solid fa-battery-full',
    'bell-fill': 'fa-solid fa-bell',
    'box-seam': 'fa-solid fa-box',
    'briefcase-fill': 'fa-solid fa-briefcase',
    'broadcast': 'fa-solid fa-tower-broadcast',
    'broadcast-pin': 'fa-solid fa-tower-broadcast',
    'building': 'fa-solid fa-building',
    'calendar-event': 'fa-solid fa-calendar-day',
    'card-text': 'fa-solid fa-address-card',
    'cash-coin': 'fa-solid fa-coins',
    'cash-stack': 'fa-solid fa-money-bill',
    'check-circle-fill': 'fa-solid fa-circle-check',
    'check-lg': 'fa-solid fa-check',
    'chevron-down': 'fa-solid fa-chevron-down',
    'clipboard-check': 'fa-solid fa-clipboard-check',
    'cloud-haze2-fill': 'fa-solid fa-smog',
    'cpu-fill': 'fa-solid fa-microchip',
    'credit-card': 'fa-solid fa-credit-card',
    'credit-card-2-front': 'fa-solid fa-credit-card',
    'cursor-fill': 'fa-solid fa-arrow-pointer',
    'dash-lg': 'fa-solid fa-minus',
    'door-closed-fill': 'fa-solid fa-door-closed',
    'droplet': 'fa-solid fa-droplet',
    'droplet-fill': 'fa-solid fa-droplet',
    'egg-fried': 'fa-solid fa-egg',
    'envelope-fill': 'fa-solid fa-envelope',
    'ethernet': 'fa-solid fa-ethernet',
    'fire': 'fa-solid fa-fire',
    'flag-fill': 'fa-solid fa-flag',
    'geo-alt-fill': 'fa-solid fa-location-dot',
    'google': 'fa-brands fa-google',
    'google-play': 'fa-brands fa-google-play',
    'graph-up-arrow': 'fa-solid fa-arrow-trend-up',
    'grid-fill': 'fa-solid fa-table-cells-large',
    'heart-fill': 'fa-solid fa-heart',
    'house': 'fa-solid fa-house',
    'house-door-fill': 'fa-solid fa-house',
    'house-exclamation-fill': 'fa-solid fa-house-circle-exclamation',
    'house-heart-fill': 'fa-solid fa-house-chimney',
    'infinity': 'fa-solid fa-infinity',
    'layers-fill': 'fa-solid fa-layer-group',
    'link-45deg': 'fa-solid fa-link',
    'list': 'fa-solid fa-bars',
    'list-nested': 'fa-solid fa-list-ul',
    'lock-fill': 'fa-solid fa-lock',
    'moisture': 'fa-solid fa-droplet',
    'moon-stars-fill': 'fa-solid fa-moon',
    'mortarboard-fill': 'fa-solid fa-graduation-cap',
    'patch-check-fill': 'fa-solid fa-circle-check',
    'pencil-square': 'fa-solid fa-pen-to-square',
    'people': 'fa-solid fa-users',
    'people-fill': 'fa-solid fa-users',
    'person-badge': 'fa-solid fa-id-badge',
    'person-fill': 'fa-solid fa-user',
    'phone-fill': 'fa-solid fa-mobile-screen-button',
    'plug-fill': 'fa-solid fa-plug',
    'plus-lg': 'fa-solid fa-plus',
    'search': 'fa-solid fa-magnifying-glass',
    'send-fill': 'fa-solid fa-paper-plane',
    'shield-check': 'fa-solid fa-shield-halved',
    'signpost-split-fill': 'fa-solid fa-signs-post',
    'slash-circle': 'fa-solid fa-ban',
    'sliders': 'fa-solid fa-sliders',
    'snow2': 'fa-solid fa-snowflake',
    'stack': 'fa-solid fa-layer-group',
    'star-fill': 'fa-solid fa-star',
    'stars': 'fa-solid fa-wand-magic-sparkles',
    'telephone-fill': 'fa-solid fa-phone',
    'thermometer-high': 'fa-solid fa-temperature-high',
    'thermometer-snow': 'fa-solid fa-temperature-low',
    'trash': 'fa-solid fa-trash',
    'tv-fill': 'fa-solid fa-tv',
    'volume-up-fill': 'fa-solid fa-volume-high',
    'warning': 'fa-solid fa-triangle-exclamation',
    'water': 'fa-solid fa-water',
    'wifi': 'fa-solid fa-wifi',
    'wind': 'fa-solid fa-wind',
    'x-lg': 'fa-solid fa-xmark'
};

const Icon = ({ className, color, name, size = 16, style }) => {

    let icon = ICONS[name];
    if(!icon) {
        // Loud in development, invisible in production — a missing icon should
        // never be the reason a page fails to render.
        if(process.env.NODE_ENV !== 'production') {
            console.warn(`Icon: no icon named "${name}"`);
        }
        return null;
    }

    /*
     * The wrapper is not decoration, it is the whole point.
     *
     * A Font Awesome kit rewrites the DOM: every <i> carrying an fa- class is
     * replaced with an <svg> after the kit loads. React does not know that
     * happened and still holds a reference to the <i>, so the next time it
     * unmounts the icon - a chip being deselected, a conditional going false -
     * removeChild is called on a node that no longer exists and the render
     * throws "The object can not be found here."
     *
     * So React is given a plain span it owns outright, with no fa- class for
     * the kit to match on, and the bare <i> inside is the kit's to replace.
     * Removing the span succeeds because the span is still a real node.
     *
     * Everything visual belongs on the wrapper for the same reason: the <i>
     * must never receive a prop React would later try to patch onto a node
     * that has been swapped out. The replaced <svg> inherits color and size
     * from the span the way text does.
     */
    return (
        <span
        className={className}
        style={{
            alignItems: 'center',
            color: color || 'currentColor',
            display: 'inline-flex',
            flexShrink: 0,
            fontSize: size,
            justifyContent: 'center',
            lineHeight: 1,
            ...style
        }}>
            <i className={icon} />
        </span>
    )
}

export default Icon;
